// Ref: https://github.com/jupyterlite/jupyterlite/blob/f2ecc9cf7189cb19722bec2f0fc7ff5dfd233d47/packages/pyolite-kernel/src/kernel.ts
import { PromiseDelegate } from "@stlite/common";
import { CrossOriginWorkerMaker as Worker } from "./cross-origin-worker";
import { normalizeBasePath } from "./uri-utils";
import { assertStreamlitConfig } from "./types";
const validateEnvKey = (key) => {
    // Validate each variable name: it must start with a letter or underscore,
    // and can contain only letters, digits, or underscores.
    const validEnvNameRegex = /^[A-Za-z_][A-Za-z0-9_]*$/;
    return validEnvNameRegex.test(key);
};
function isSharedWorker(worker) {
    // `SharedWorker` is not available in some environments like Chrome for Android,
    // so we need to check if it is available before using it.
    return (typeof window.SharedWorker !== "undefined" && worker instanceof SharedWorker);
}
export class StliteKernel extends EventTarget {
    _isDisposed = false;
    _worker;
    _postMessageTarget;
    _loaded = new PromiseDelegate();
    _workerInitData;
    basePath; // TODO: Move this prop to outside this class. This is not a member of the kernel business logic, but just a globally referred value.
    hostConfigResponse; // Will be passed to ConnectionManager to call `onHostConfigResp` from it.
    addEventListener(type, listener, options) {
        super.addEventListener(type, listener, options);
    }
    removeEventListener(type, listener, options) {
        super.removeEventListener(type, listener, options);
    }
    dispatchEvent(event) {
        return super.dispatchEvent(event);
    }
    constructor(options) {
        super();
        this.basePath = normalizeBasePath(options.basePath ?? window.location.pathname);
        this.hostConfigResponse = options.hostConfigResponse ?? {};
        if (options.worker) {
            this._worker = options.worker;
        }
        else {
            // HACK: Use `CrossOriginWorkerMaker` imported as `Worker` here.
            // Read the comment in `cross-origin-worker.ts` for the detail.
            const workerMaker = new Worker(new URL("./worker.js", import.meta.url), {
                /* @vite-ignore */ // To avoid the Vite error: "[vite:worker-import-meta-url] Vite is unable to parse the worker options as the value is not static.To ignore this error, please use /* @vite-ignore */ in the worker options."
                type: options.workerType,
                shared: options.sharedWorker ?? false,
            });
            this._worker = workerMaker.worker;
        }
        if (isSharedWorker(this._worker)) {
            this._worker.port.start();
            this._postMessageTarget = this._worker.port;
        }
        else {
            this._postMessageTarget = this._worker;
        }
        this._postMessageTarget.onmessage = (e) => {
            this._processWorkerMessage(e.data, e.ports[0]);
        };
        // TODO: Assert other options as well.
        if (options.streamlitConfig != null) {
            assertStreamlitConfig(options.streamlitConfig);
        }
        this._workerInitData = {
            entrypoint: options.entrypoint,
            files: options.files,
            archives: options.archives,
            requirements: options.requirements,
            prebuiltPackageNames: options.prebuiltPackageNames,
            pyodideUrl: options.pyodideUrl,
            wheels: options.wheelUrls,
            streamlitConfig: options.streamlitConfig,
            idbfsMountpoints: options.idbfsMountpoints,
            moduleAutoLoad: options.moduleAutoLoad ?? false,
            env: options.env,
            languageServer: options.languageServer ?? false,
        };
    }
    get loaded() {
        return this._loaded.promise;
    }
    connectWebSocket(path) {
        return this._asyncPostMessage({
            type: "websocket:connect",
            data: {
                path,
            },
        }).then();
    }
    sendWebSocketMessage(payload) {
        return this._asyncPostMessage({
            type: "websocket:send",
            data: {
                payload,
            },
        }).then();
    }
    handleWebSocketMessage = null;
    onWebSocketMessage(handler) {
        this.handleWebSocketMessage = handler;
    }
    sendHttpRequest(request) {
        return this._asyncPostMessage({
            type: "http:request",
            data: {
                request,
            },
        }, "http:response").then((data) => {
            return {
                ...data.response,
                headers: new Headers(Object.fromEntries(data.response.headers)),
            };
        });
    }
    writeFile(path, data, opts) {
        const promise = this._asyncPostMessage({
            type: "file:write",
            data: {
                path,
                data,
                opts,
            },
        }).then(() => { });
        this.dispatchEvent(new CustomEvent("writeFile", {
            detail: {
                path,
                promise,
            },
        }));
        return promise;
    }
    renameFile(oldPath, newPath) {
        const promise = this._asyncPostMessage({
            type: "file:rename",
            data: {
                oldPath,
                newPath,
            },
        }).then(() => { });
        this.dispatchEvent(new CustomEvent("renameFile", {
            detail: {
                oldPath,
                newPath,
                promise,
            },
        }));
        return promise;
    }
    readFile(path, opts) {
        const promise = this._asyncPostMessage({
            type: "file:read",
            data: {
                path,
                opts,
            },
        }, "reply:file:read").then((data) => data.content);
        this.dispatchEvent(new CustomEvent("readFile", {
            detail: {
                path,
                promise,
            },
        }));
        return promise;
    }
    unlink(path) {
        return this._asyncPostMessage({
            type: "file:unlink",
            data: {
                path,
            },
        }).then();
    }
    install(requirements, options) {
        const promise = this._asyncPostMessage({
            type: "install",
            data: {
                requirements,
                options,
            },
        }).then(() => { });
        this.dispatchEvent(new CustomEvent("install", {
            detail: {
                requirements,
                promise,
            },
        }));
        return promise;
    }
    setEnv(env) {
        Object.keys(env).forEach((key) => {
            if (!validateEnvKey(key)) {
                throw new Error(`Invalid environment variable name: "${key}"`);
            }
        });
        return this._asyncPostMessage({
            type: "setEnv",
            data: {
                env,
            },
        }).then();
    }
    getCodeCompletion(code, position) {
        if (!this._workerInitData.languageServer) {
            throw new Error(`Language server not loaded, please set languageServer=true to use this method`);
        }
        return this._asyncPostMessage({
            type: "code_completion",
            data: {
                code,
                line: position.line,
                column: position.column,
            },
        }, "reply:code_completion").then((data) => data.codeCompletions);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    runPython(code) {
        return this.loaded
            .then(() => this._asyncPostMessage({
            type: "run_python",
            data: {
                code,
            },
        }, "reply:run_python"))
            .then((data) => data.result);
    }
    /**
     * Reboot the Streamlit server.
     * Note that we also need to refresh (rerender) the frontend app after calling this method
     * to reflect the changes on the user-facing side.
     */
    reboot(entrypoint) {
        return this._asyncPostMessage({
            type: "reboot",
            data: {
                entrypoint,
            },
        }).then();
    }
    _asyncPostMessage(message, expectedReplyType = "reply") {
        return new Promise((resolve, reject) => {
            const channel = new MessageChannel();
            channel.port1.onmessage = (e) => {
                channel.port1.close();
                const msg = e.data;
                if (msg.error) {
                    reject(msg.error);
                }
                else {
                    if (msg.type !== expectedReplyType) {
                        throw new Error(`Unexpected reply type "${msg.type}"`);
                    }
                    resolve(msg.data);
                }
            };
            this._postMessageTarget.postMessage(message, [channel.port2]);
        });
    }
    /**
     * Process a message coming from the pyodide web worker.
     *
     * @param msg The worker message to process.
     */
    _processWorkerMessage(msg, port) {
        switch (msg.type) {
            case "event:envSetup": {
                this._postMessageTarget.postMessage({
                    type: "initData",
                    data: this._workerInitData,
                });
                break;
            }
            case "event:loadProgress": {
                this.dispatchEvent(new CustomEvent("loadProgress", { detail: msg.data.message }));
                break;
            }
            case "event:loadError": {
                this.dispatchEvent(new CustomEvent("loadError", { detail: msg.data.error }));
                break;
            }
            case "event:loadFinished": {
                this._loaded.resolve();
                this.dispatchEvent(new Event("loadFinished"));
                break;
            }
            case "websocket:message": {
                const { payload } = msg.data;
                this.handleWebSocketMessage?.(payload);
                break;
            }
            case "event:moduleAutoLoad": {
                if (port == null) {
                    throw new Error("Port is required for moduleAutoLoad event");
                }
                const promise = new Promise((resolve, reject) => {
                    port.onmessage = (e) => {
                        const msg = e.data;
                        if (msg.type === "moduleAutoLoad:success") {
                            resolve(msg.data.loadedPackages);
                        }
                        else {
                            reject(msg.error);
                        }
                        port.close();
                    };
                });
                this.dispatchEvent(new CustomEvent("moduleAutoLoad", {
                    detail: {
                        packagesToLoad: msg.data.packagesToLoad,
                        promise,
                    },
                }));
                break;
            }
        }
    }
    /**
     * Return whether the kernel is disposed.
     */
    get isDisposed() {
        return this._isDisposed;
    }
    /**
     * Dispose the kernel.
     */
    dispose() {
        if (this.isDisposed) {
            return;
        }
        if (isSharedWorker(this._worker)) {
            this._worker.port.close();
        }
        else {
            this._worker.terminate();
        }
        this._isDisposed = true;
    }
}
//# sourceMappingURL=kernel.js.map